import { Trans } from '@lingui/react/macro';
import Analyzer from 'parser/core/Analyzer';
import Abilities from 'parser/core/modules/Abilities';
import Haste from 'parser/shared/modules/Haste';
import SpellHistory from 'parser/shared/modules/SpellHistory';
import CastEfficiencyComponent from 'parser/ui/CastEfficiency';
import Panel from 'parser/ui/Panel';
import Spell from 'common/SPELLS/Spell';

import Combatant from '../../core/Combatant';
import { EventType, UpdateSpellUsableEvent, UpdateSpellUsableType } from '../../core/Events';
import Ability, { SpellbookAbility } from '../../core/modules/Ability';
import AbilityTracker from './AbilityTracker';

const DEFAULT_RECOMMENDED = 0.8;
const DEFAULT_AVERAGE_DOWNSTEP = 0.05;
const DEFAULT_MAJOR_DOWNSTEP = 0.15;
const seconds = (ms: number) => ms / 1000;
const minutes = (ms: number) => seconds(ms) / 60;

export interface AbilityCastEfficiency {
  ability: Ability;
  cpm: number;
  casts: number;
  maxCasts: number;
  efficiency: number | null;
  recommendedEfficiency: number;
  averageIssueEfficiency: number;
  majorIssueEfficiency: number;
  gotMaxCasts: boolean;
  canBeImproved?: boolean;
}

class CastEfficiency extends Analyzer {
  static dependencies = {
    abilityTracker: AbilityTracker,
    haste: Haste,
    spellHistory: SpellHistory,
    abilities: Abilities,
  };
  protected abilityTracker!: AbilityTracker;
  protected haste!: Haste;
  protected spellHistory!: SpellHistory;
  protected abilities!: Abilities;

  /**
   * Gets info about spell's cooldown behavior. All values are as of the current timestamp.
   * completedRechargeTime is the total ms of completed cooldowns
   * endingRechargeTime is the total ms into current cooldown
   * recharges is the total number of times the spell has recharged (either come off cooldown or gained a charge)
   * Only works on spells entered into CastEfficiency list.
   */
  // oxlint-disable-next-line typescript-eslint/no-explicit-any -- Baseline suppression. Try to fix if you edit this code.
  private getCooldownInfo(ability: any) {
    const mainSpellId = ability.primarySpell;
    const history = this.spellHistory.historyBySpellId[mainSpellId];
    if (!history) {
      // spell either never been cast, or not in abilities list
      return {
        completedRechargeTime: 0,
        endingRechargeTime: 0,
        recharges: 0,
        casts: 0,
        castTimestamps: [],
      };
    }

    let lastRechargeTimestamp: number | undefined = undefined;
    let recharges = 0;
    const completedRechargeTime = history
      .filter(
        (event): event is UpdateSpellUsableEvent => event.type === EventType.UpdateSpellUsable,
      )
      .reduce((acc, event) => {
        if (event.updateType === UpdateSpellUsableType.BeginCooldown) {
          lastRechargeTimestamp = event.timestamp;
          return acc;
        } else if (
          event.updateType === UpdateSpellUsableType.EndCooldown &&
          //We're only interested in events that happened during the fight - any events outside of that are faked
          //The remaining time between last and fight endtime is counted as recharged time in endingRechargeTime
          event.timestamp <= this.owner.fight.end_time
        ) {
          //limit by start time in case of pre phase events
          recharges += 1;
          const timeSinceLastRecharge = event.timestamp - (lastRechargeTimestamp || 0);
          lastRechargeTimestamp = undefined;
          // this is just event.timePassed except `endcooldown` events
          // don't have `timePassed` filled in.
          return acc + timeSinceLastRecharge;
          // This might cause oddness if we add anything that externally refreshes charges, but so far nothing does
        } else if (event.updateType === UpdateSpellUsableType.RestoreCharge) {
          //limit by start time in case of pre phase events
          recharges += 1;
          const timeSinceLastRecharge = event.timestamp - (lastRechargeTimestamp || 0);
          lastRechargeTimestamp = event.timestamp;
          return acc + timeSinceLastRecharge;
        } else {
          return acc;
        }
      }, 0);
    //limit by start time in case of pre phase events
    const endingRechargeTime = !lastRechargeTimestamp
      ? 0
      : this.owner.currentTimestamp - Math.max(lastRechargeTimestamp, this.owner.fight.start_time);

    /** Empower Abilities trigger Cast event on start of channel therefore
     * EmpowerEnd event is the proper event to track instead */
    const isEmpowerAbility = history.some((event) => event.type === EventType.EmpowerEnd);
    const castEvents = isEmpowerAbility
      ? history.filter((event) => event.type === EventType.EmpowerEnd)
      : history.filter((event) => event.type === EventType.Cast);
    const casts = castEvents.length;
    const castTimestamps = castEvents.map((event) => event.timestamp - this.owner.fight.start_time);

    return {
      completedRechargeTime,
      endingRechargeTime,
      recharges,
      casts,
      castTimestamps,
    };
  }

  private _getTimeSpentCasting(spellId: number) {
    const history = this.spellHistory.historyBySpellId[spellId];
    if (!history) {
      // spell either never been cast, or not in abilities list
      return 0;
    }
    const isEmpowerAbility = history.some((event) => event.type === EventType.EmpowerEnd);

    let beginCastTimestamp: number | undefined;
    const timeSpentCasting = history.reduce((acc, event) => {
      if (event.type === EventType.BeginCast || event.type === EventType.EmpowerStart) {
        beginCastTimestamp = event.timestamp;
        return acc;
      } else if (
        (event.type === EventType.Cast && !isEmpowerAbility) ||
        event.type === EventType.EmpowerEnd
      ) {
        //limit by start time in case of pre phase events
        const castTime = beginCastTimestamp
          ? event.timestamp - Math.max(beginCastTimestamp, this.owner.fight.start_time)
          : 0;
        beginCastTimestamp = undefined;
        return acc + castTime;
      } else {
        return acc;
      }
    }, 0);
    return timeSpentCasting;
  }

  private getGcd(gcd: SpellbookAbility['gcd']) {
    const getGcdValue = (value: ((combatant: Combatant) => number) | number) =>
      typeof value === 'function' ? value(this.owner.selectedCombatant) : value;

    if (typeof gcd === 'function') {
      return gcd(this.owner.selectedCombatant);
    } else if (gcd && gcd.static) {
      return getGcdValue(gcd.static);
    } else if (gcd && gcd.base) {
      const base = getGcdValue(gcd.base);
      const minimum = gcd.minimum ? getGcdValue(gcd.minimum) : base / 2;
      const gcdReduction = base * this.haste.current;
      const gcdActual = Math.max(minimum, base - gcdReduction);

      return gcdActual;
    }
    return undefined;
  }

  private getTimeSpentOnGcd(spellId: number) {
    const ability = this.abilities.getAbility(spellId);

    if (ability && ability.gcd) {
      const cdInfo = this.getCooldownInfo(ability);

      let casts;
      if (ability.castEfficiency.casts) {
        casts = ability.castEfficiency.casts(this.abilityTracker.getAbility(spellId), this.owner);
      } else {
        casts = cdInfo.casts;
      }

      const averagetimeSpentOnAbility = this._getTimeSpentCasting(ability.primarySpell) / casts;
      // If the abilities GCD is longer than the time spent casting, use the GCD
      const gcd = this.getGcd(ability.gcd);
      if (gcd && gcd > averagetimeSpentOnAbility) {
        return gcd * casts;
      }
    }
    return 0;
  }

  getTimeSpentCasting(abilityId: number) {
    const timeSpentCasting = this._getTimeSpentCasting(abilityId);
    const gcdSpent = this.getTimeSpentOnGcd(abilityId);

    return {
      timeSpentCasting,
      gcdSpent,
    };
  }

  /**
   * Time spent waiting for a GCD that reset the cooldown of the spell to finish
   */
  private _getTimeWaitingOnGCD(spellId: number) {
    const history = this.spellHistory.historyBySpellId[spellId];
    if (!history) {
      // spell either never been cast, or not in abilities list
      return 0;
    }

    const timeWaitingOnGCD = history.reduce((acc, event) => {
      if (event.type === EventType.UpdateSpellUsable && event.timeWaitingOnGCD) {
        return acc + event.timeWaitingOnGCD;
      } else {
        return acc;
      }
    }, 0);

    return timeWaitingOnGCD;
  }

  /**
   * Packs cast efficiency results for use by suggestions / tab
   */
  getCastEfficiency() {
    return this.abilities.activeAbilities
      .map((ability) => this.getCastEfficiencyForAbility(ability))
      .filter((item) => item !== null) as AbilityCastEfficiency[]; // getCastEfficiencyForAbility can return null, remove those from the result
  }

  getTotalCastCount(): number {
    return this.getCastEfficiency().reduce((totalCasts, ability) => totalCasts + ability.casts, 0);
  }

  getCastEfficiencyForSpellId(
    spellId: number,
    includeNoCooldownEfficiency = false,
  ): AbilityCastEfficiency | null {
    const ability = this.abilities.getAbility(spellId);
    return ability ? this.getCastEfficiencyForAbility(ability, includeNoCooldownEfficiency) : null;
  }

  getCastEfficiencyForSpell(
    spell: Spell,
    includeNoCooldownEfficiency = false,
  ): AbilityCastEfficiency | null {
    return this.getCastEfficiencyForSpellId(spell.id, includeNoCooldownEfficiency);
  }

  getCastEfficiencyForAbility(
    ability: Ability,
    includeNoCooldownEfficiency = false,
  ): AbilityCastEfficiency | null {
    const spellId = ability.primarySpell;
    const availableFightDuration = this.owner.fightDuration;

    const cooldown = ability.cooldown;
    const cooldownMs = !cooldown ? null : cooldown * 1000;
    const cdInfo = this.getCooldownInfo(ability);
    const timeSpentCasting =
      cooldown && ability.charges < 2 ? this._getTimeSpentCasting(ability.primarySpell) : 0;
    const timeWaitingOnGCD =
      cooldown && ability.charges < 2 && ability.gcd
        ? this._getTimeWaitingOnGCD(ability.primarySpell)
        : 0;

    // ability.casts is used for special cases that show the wrong number of cast events, like Penance
    // and also for splitting up differently buffed versions of the same spell
    let casts;
    if (ability.castEfficiency.casts) {
      casts = ability.castEfficiency.casts(this.abilityTracker.getAbility(spellId), this.owner);
    } else {
      casts = cdInfo.casts;
    }
    const cpm = casts / minutes(availableFightDuration);
    const averageTimeSpentCasting = timeSpentCasting / casts;
    const averageTimeWaitingOnGCD = timeWaitingOnGCD / casts;

    if (ability.isUndetectable && casts === 0) {
      // Some spells (most notably Racials) can not be detected if a player has them. This hides those spells if they have 0 casts.
      return null;
    }

    // ability.maxCasts is used for special cases for spells that have a variable availability or CD based on state, like Void Bolt.
    // This same behavior should be managable using SpellUsable's interface, so maxCasts is deprecated.
    // Legacy support: if maxCasts is defined, cast efficiency will be calculated using casts/rawMaxCasts
    let rawMaxCasts: number | undefined;
    const averageCooldown =
      cdInfo.recharges === 0 ? null : cdInfo.completedRechargeTime / cdInfo.recharges;
    if (ability.castEfficiency.maxCasts) {
      // maxCasts expects cooldown in seconds
      rawMaxCasts = ability.castEfficiency.maxCasts(cooldown);
    } else if (averageCooldown) {
      // no average CD if spell hasn't been cast
      rawMaxCasts =
        availableFightDuration /
          (averageCooldown + averageTimeSpentCasting + averageTimeWaitingOnGCD) +
        (ability.charges || 1) -
        1;
    } else if (!includeNoCooldownEfficiency) {
      rawMaxCasts = availableFightDuration / cooldownMs! + (ability.charges || 1) - 1;
    } else if (casts > 0) {
      let averagetimeSpentOnNoCooldownAbility =
        this._getTimeSpentCasting(ability.primarySpell) / casts;
      const gcd = this.getGcd(ability.gcd);
      if (gcd && averagetimeSpentOnNoCooldownAbility < gcd) {
        // edge case for no cast time spells and spells with cast time lower than the gcd.
        averagetimeSpentOnNoCooldownAbility = gcd;
      }
      rawMaxCasts = availableFightDuration / averagetimeSpentOnNoCooldownAbility;
    } else {
      // If we don't have any way to tell the cast time of the spell, return null.
      rawMaxCasts = undefined;
    }

    // rawMaxCasts is actually the number of cooldowns you could fully restore during the fights.
    // So if you could restore 3 full CDs of 60s during a fight of 185s, it means that you can cast at
    // 1) t=0s
    // 2) t=60s
    // 3) t=120s
    // 4) t=180s
    // which is 4 times. But in this case rawMaxCasts=3.08333.
    // The solution is thus to count the CDs that can be fully restored (here 3) and
    // add 1 because we are counting the number of casts and not the number of CDs.
    // (same as counting the number of pickets based on a picket fence length and the total length of the fence).
    const maxCasts = Math.floor(rawMaxCasts || 0) + 1;

    let efficiency;
    if (ability.castEfficiency.maxCasts) {
      // legacy support for custom maxCasts
      efficiency = Math.min(1, casts / rawMaxCasts! || 0);
    } else {
      // Cast efficiency calculated as the percent of fight time spell was unavailable
      // The spell is considered unavailable if it is on cooldown, the time since it came off cooldown is less than the cast time or the cooldown was reset through a proc during a GCD
      if (cooldown && availableFightDuration) {
        const timeOnCd = cdInfo.completedRechargeTime + cdInfo.endingRechargeTime;
        const timeUnavailable = timeOnCd + timeSpentCasting + timeWaitingOnGCD;
        efficiency = timeUnavailable / availableFightDuration;
      } else if (includeNoCooldownEfficiency) {
        efficiency = casts / rawMaxCasts!;
      } else {
        efficiency = null;
      }
    }

    const recommendedEfficiency =
      ability.castEfficiency.recommendedEfficiency || DEFAULT_RECOMMENDED;
    const averageIssueEfficiency =
      ability.castEfficiency.averageIssueEfficiency ||
      recommendedEfficiency - DEFAULT_AVERAGE_DOWNSTEP;
    const majorIssueEfficiency =
      ability.castEfficiency.majorIssueEfficiency || recommendedEfficiency - DEFAULT_MAJOR_DOWNSTEP;

    const gotMaxCasts = casts === maxCasts;
    const canBeImproved =
      ability.castEfficiency.suggestion &&
      efficiency !== null &&
      efficiency < recommendedEfficiency &&
      !gotMaxCasts;

    return {
      ability,
      cpm,
      casts,
      maxCasts,
      efficiency,
      recommendedEfficiency,
      averageIssueEfficiency,
      majorIssueEfficiency,
      gotMaxCasts,
      canBeImproved,
    };
  }

  statistic() {
    return (
      <Panel title={<Trans id="common.abilities">Abilities</Trans>} position={500} pad={false}>
        <CastEfficiencyComponent abilities={this.getCastEfficiency()} />
      </Panel>
    );
  }
}

export default CastEfficiency;
